import { confirmBox } from '../components/dialog.js';
import { renderHeader } from '../components/header.js';
import { renderNav } from '../components/nav.js';
import { loadProgramDays, loadDayExercises, loadLastSets, loadSessions, loadRuns, loadRunPlan, saveRun, moveSession, loadFoodTotalsByDate, loadSettings, loadGoals, loadActiveGoal, loadFoodEntries, loadWeights, loadGoalCheckins, checkInGoal, completeGoal, dropGoal, loadLiftNotes } from '../lib/grit-data.js';
import { displayNameHtml, ensureNames } from '../lib/exercise-names.js';
import { goalProgress, canCheckInToday } from '../lib/weekly-goal.js';
import { openWeeklyGoalSheet, openCompletedGoals } from '../components/weekly-goal-sheet.js';
import { toast } from '../components/toast.js';
import { planWeekNumber } from '../lib/run-plan.js';
import { scheduleWeek, logTypeFor } from '../lib/run-schedule.js';
import { scheduleFor } from './running.js';
import { openScheduleEditor } from '../components/schedule-sheet.js';
import { openRunLog } from '../components/run-log-sheet.js';
import { applyRunSideEffects } from '../lib/run-progress.js';
import { go } from '../router.js';
import { fmtWeight, repRange } from '../lib/formatters.js';
import { isViewingSelf, viewing } from '../lib/auth.js';
import { loadProgress, clearProgress, progressCounts } from '../lib/progress.js';
import { maybeShowOnboarding } from '../components/onboarding.js';
import { openProgramSetup } from '../components/program-setup.js';
import { openExerciseEdit } from '../components/exercise-edit-sheet.js';
import { icon } from '../components/icons.js';
import { phaseInfo, phaseLabel, localISO } from '../lib/phase.js';
import { doneThisWeek, suggestDay, startDateFor } from '../lib/week-swap.js';
import { caloriesLeft } from '../lib/inbox.js';
import { bodyCard } from '../components/body-card.js';
import { openNotes } from '../components/notes-sheet.js';
import { notesByLift } from '../lib/lift-notes.js';

const WD = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const todayWeekday = () => { const d = new Date().getDay(); return d === 0 ? 7 : d; };
const todayISO = () => localISO();

function homeView() { return localStorage.getItem('grit-home-view') || 'today'; }
function setHomeView(v) { localStorage.setItem('grit-home-view', v); }

function viewToggle(view) {
  return `<div class="grit-seg mb-5 max-w-[240px]">
    ${[['today', 'Today'], ['calendar', 'Calendar']].map(([v, lbl]) =>
      `<button data-view="${v}" class="${view === v ? 'on' : ''}">${lbl}</button>`).join('')}
  </div>`;
}

export function renderDay(el) {
  maybeShowOnboarding();
  if (homeView() === 'calendar') return renderCalendar(el);
  return renderToday(el);
}

function projectMinutes(exs) {
  let sec = 0;
  for (const e of exs) { const sets = e.target_sets || 3; sec += sets * 45 + Math.max(0, sets - 1) * (e.rest_seconds || 120); }
  return Math.round(sec / 60);
}

// The viewed user's current run week, generated from the single distance-plan
// source (so Today and the Running tab never disagree). Set in the loaders.
let curRunWeek = null;
// Today reads the same saved running plan as the Running tab (plan v4: one source).
function weekForPlan(plan) { return scheduleWeek(scheduleFor(plan, viewing())); }
function runInSlot(slot) { return curRunWeek?.runs.find((r) => r.slot === slot) || null; }
function runOnWeekday(wd) { return curRunWeek?.runs.find((r) => r.weekday === wd) || null; }

// Directions for a run-kind program-day (matched by weekday to the generated week).
function runDirections(day) { return runOnWeekday(day.weekday)?.detail || ''; }

// Users with a run plan but no run program-days (Kenny) get their runs woven onto
// Today by weekday. Users whose runs are real program-days are left to runDirections.
function planRunMap(_plan, allDays) {
  if (!curRunWeek || allDays.some((d) => d.kind === 'run')) return {};
  const map = {};
  curRunWeek.runs.forEach((r) => { map[r.weekday] = r.slot; });
  return map;
}
function runSlotDirections(slot) { return runInSlot(slot)?.detail || ''; }
function runSlotName(slot) { return runInSlot(slot)?.title || (slot === 'long' ? 'Long Run' : 'Run'); }
function runSlotType(slot) { const r = runInSlot(slot); return r ? logTypeFor(r) : 'easy'; }
function runSlotMinutes(slot) { return runInSlot(slot)?.minutes ?? null; }
function runSlotDistance(slot) { return runInSlot(slot)?.distanceMi ?? null; }
function planContextFor(slot, plan) {
  return { week: planWeekNumber(plan?.race_date), slot, plannedDistanceMi: runSlotDistance(slot), plannedMinutes: runSlotMinutes(slot), type: runSlotType(slot) };
}

// Post-save work shared by every run logged from Today/Calendar: the mile/easy-pace
// side effects (same for both users since 2026-10-22).
async function afterRunSaved(run) {
  await applyRunSideEffects(run);
}

// ── Today ───────────────────────────────────────────────────────────────────
function renderToday(el) {
  let allDays = [];
  let plan = null;
  let runsToday = [];
  let selected = null;
  let notesByEx = {};
  let goal = null;
  let checkins = [];
  let cal = null;
  let settings = {};
  const tw = todayWeekday();
  const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5"><p class="text-inksoft">Loading…</p></div>${renderNav('home')}`;

  (async () => {
    let goals, runs;
    [allDays, plan, settings, goals, runs] = await Promise.all([loadProgramDays(), loadRunPlan(), loadSettings(), loadGoals(), loadRuns()]);
    await ensureNames(viewing());
    notesByEx = notesByLift(await loadLiftNotes());
    goal = await loadActiveGoal(viewing());
    const [weights, food] = await Promise.all([loadWeights(viewing()), loadFoodEntries(todayISO(), viewing())]);
    cal = caloriesLeft({ latestWeightLb: weights.find((w) => w.weight_lb != null)?.weight_lb, settings, eaten: food.reduce((a, e) => a + Number(e.calories || 0), 0), date: todayISO() });
    checkins = goal ? await loadGoalCheckins(goal.id) : [];
    curRunWeek = weekForPlan(plan);
    runsToday = runs.filter((r) => r.date === todayISO());
    selected = allDays.find((d) => d.weekday === tw) || null;
    paint();
  })();

  // Calories left today, always at the top (Kenny, 2026-10-25). Tap to open Food.
  function calBar() {
    if (!cal?.ready) return '';
    const over = cal.left < 0;
    const pct = Math.max(0, Math.min(100, Math.round((cal.eaten / cal.target) * 100)));
    return `<a href="#/food" class="grit-card p-4 mb-3 block active:scale-[0.99] transition-transform">
      <div class="flex items-baseline justify-between">
        <div class="text-[15px] text-inksoft">Calories ${over ? 'over' : 'left'} today</div>
        <div class="font-rounded font-bold tabular-nums text-[22px] ${over ? 'text-danger' : 'text-ink'}">${Math.abs(cal.left).toLocaleString('en-US')}</div>
      </div>
      <div class="h-2 rounded-full bg-fill mt-2 overflow-hidden"><div class="h-full rounded-full ${over ? 'bg-danger' : 'bg-brand'}" style="width:${pct}%"></div></div>
      <div class="text-[13px] text-inksoft mt-1.5 tabular-nums">${cal.eaten.toLocaleString('en-US')} of ${cal.target.toLocaleString('en-US')} eaten</div>
    </a>`;
  }

  function focusCard() {
    const self = isViewingSelf();
    if (!goal) {
      if (!self) return '';
      return `<div class="grit-card p-4 mb-3 ">
        <div class="flex items-start justify-between gap-2">
          <button id="focus-set" class="text-left min-w-0 active:scale-[0.99] transition-transform">
            <div class="grit-eyebrow !text-grit-press">This week's focus</div>
            <div class="font-bold text-ink text-[15px] mt-0.5">Set one goal for the week</div>
            <div class="text-[13px] text-inksoft mt-0.5">One thing. Track it daily. Earn the reward.</div>
          </button>
          <button id="focus-done-list" class="grit-pill flex-none">Completed</button>
        </div>
      </div>`;
    }
    const p = goalProgress(goal, checkins);
    const canCheck = self && canCheckInToday(checkins, todayISO()) && !p.done;
    return `<div class="grit-card p-4 mb-3 ">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0"><div class="grit-eyebrow ${p.done ? '!text-success' : '!text-grit-press'}">This week's focus</div>
          <div class="font-bold text-ink text-[15px] truncate">${goal.text.replace(/</g, '&lt;')}</div></div>
        <button id="focus-done-list" class="grit-pill flex-none">Completed</button>
      </div>
      <div class="mt-2 h-2 rounded-full bg-pagebg overflow-hidden"><div class="h-full ${p.done ? 'bg-success' : 'bg-grit'}" style="width:${p.pct}%"></div></div>
      <div class="flex items-center justify-between mt-1.5 text-[13px]">
        <span class="text-inksoft">${p.days} of ${p.target} days${goal.reward ? ' · Reward: ' + goal.reward.replace(/</g, '&lt;') : ''}</span>
        ${p.done ? '<span class="text-success font-bold">Crushed it</span>' : ''}
      </div>
      ${canCheck ? '<button id="focus-checkin" class="grit-btn mt-3">Did it today</button>' : (self && !p.done ? '<div class="grit-chip-success mt-3 inline-flex">Checked in today</div>' : '')}
      ${self && p.done ? '<button id="focus-complete" class="grit-btn-dark mt-3">Claim it &amp; finish</button>' : ''}
      ${self && !p.done ? '<button id="focus-drop" class="w-full text-inksoft text-[13px] font-bold py-2 mt-1">Drop this goal</button>' : ''}
    </div>`;
  }
  function bindFocus() {
    const set = document.getElementById('focus-set');
    if (set) set.onclick = () => openWeeklyGoalSheet(() => renderDay(el));
    const list = document.getElementById('focus-done-list');
    if (list) list.onclick = () => openCompletedGoals();
    const ci = document.getElementById('focus-checkin');
    if (ci) ci.onclick = async () => {
      const ok = await checkInGoal(goal.id, todayISO());
      if (!ok) { toast("Check-in didn't save, try again", { tone: 'bad' }); return; }
      checkins = await loadGoalCheckins(goal.id); renderDay(el);
    };
    const comp = document.getElementById('focus-complete');
    if (comp) comp.onclick = async () => {
      const ok = await completeGoal(goal.id);
      if (!ok) { toast("Couldn't finish it, try again", { tone: 'bad' }); return; }
      renderDay(el);
    };
    const drop = document.getElementById('focus-drop');
    if (drop) drop.onclick = async () => {
      if (!(await confirmBox({ title: "Drop this week's goal?", confirmLabel: 'Drop', danger: true }))) return;
      const ok = await dropGoal(goal.id);
      if (!ok) { toast("Couldn't drop it, try again", { tone: 'bad' }); return; }
      renderDay(el);
    };
  }

  function header(title, eyebrow) {
    return `<div class="flex items-start justify-between gap-2">
      <div class="min-w-0"><div class="grit-eyebrow">${eyebrow}</div><h1 class="grit-title mt-0.5 truncate">${title}</h1>${phaseLine()}</div>
      ${isViewingSelf() ? `<div class="flex items-center gap-2 flex-none">
        <button id="open-history" class="grit-pill">History</button>
        <button id="edit-sched" class="grit-pill">Edit days</button>
      </div>` : ''}
    </div>`;
  }
  function phaseLine() {
    const ph = phaseInfo(settings?.phase_start, todayISO());
    if (!ph) return '';
    return ph.deload
      ? '<div class="text-[15px] font-semibold text-grit-press mt-1">Deload week: lighter on purpose (p.332)</div>'
      : `<div class="text-[15px] text-inksoft mt-1">${phaseLabel(ph)}</div>`;
  }
  function picker() {
    return `<div class="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5 mt-3" style="scrollbar-width:none">
      ${allDays.map((d) => {
        const active = selected && d.id === selected.id;
        return `<button data-day="${d.id}" class="flex-none grit-pill ${active ? '!bg-ink !text-white' : '!bg-card'}">${WD[d.weekday]} · ${d.name}</button>`;
      }).join('')}
    </div>`;
  }
  function resumeBanner() {
    if (!isViewingSelf()) return '';
    const p = loadProgress();
    if (!p || !Array.isArray(p.cards) || !p.cards.length) return '';
    const { done, total } = progressCounts(p);
    if (!p.sessionId && done === 0) return '';
    const d = allDays.find((x) => x.id === p.dayId);
    const name = d?.name || 'Your workout';
    return `<div class="grit-card p-4 mb-3  flex items-center gap-3">
      <div class="flex-1 min-w-0">
        <div class="grit-eyebrow !text-grit-press">In progress</div>
        <div class="font-bold text-ink text-[15px] truncate">${name}</div>
        <div class="text-[13px] text-inksoft">${done} of ${total} sets logged</div>
      </div>
      <button id="resume-go" class="grit-btn !w-auto !min-h-[44px] px-5 !text-[15px] flex-none">Resume</button>
      <button id="resume-x" aria-label="Dismiss" class="flex-none w-11 h-11 -mr-2 flex items-center justify-center text-inksoft">${icon.x('w-4 h-4')}</button>
    </div>`;
  }
  function bindResume() {
    const rg = document.getElementById('resume-go');
    if (rg) rg.onclick = () => { const p = loadProgress(); if (p?.dayId) { sessionStorage.setItem('grit-day', p.dayId); go('logger'); } };
    const rx = document.getElementById('resume-x');
    if (rx) rx.onclick = () => { clearProgress(); renderDay(el); };
  }
  function bindCommon() {
    bindToggle(el, renderDay);
    bindResume();
    bindFocus();
    const es = document.getElementById('edit-sched');
    if (es) es.onclick = () => openScheduleEditor(allDays, () => renderDay(el));
    const oh = document.getElementById('open-history');
    if (oh) oh.onclick = () => go('stats');
    el.querySelectorAll('[data-day]').forEach((b) => { b.onclick = () => { selected = allDays.find((d) => d.id === b.dataset.day); paint(); }; });
  }

  async function paint() {
    const runMap = planRunMap(plan, allDays);
    if (!allDays.length) {
      const self = isViewingSelf();
      el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5">${viewToggle('today')}
        <div class="grit-card p-6 text-center mt-2">
          <div class="w-14 h-14 rounded-2xl bg-grit-soft flex items-center justify-center mx-auto mb-3"><span class="text-grit text-2xl font-bold">G</span></div>
          <div class="font-bold text-ink text-[17px]">${self ? 'Set up your training' : 'No program yet'}</div>
          <div class="text-[15px] text-inksoft mt-1 mb-4">${self ? 'Pick a starter split and start logging in under a minute. Every exercise, set, and day is editable after.' : 'Nothing set up on this account yet.'}</div>
          ${self ? '<button id="setup" class="grit-btn">Set up training</button>' : ''}
        </div></div>${renderNav('home')}`;
      bindToggle(el, renderDay);
      const su = document.getElementById('setup');
      if (su) su.onclick = () => openProgramSetup(() => renderDay(el));
      return;
    }
    if (!selected) {
      const runSlot = runMap[tw];
      if (runSlot) {
        // No lift scheduled today, but the run plan calls for a run.
        const dirs = runSlotDirections(runSlot);
        const doneToday = runsToday.some((r) => r.type !== 'hiit');
        el.innerHTML = `${renderHeader()}
          <div class="grit-page px-5 pt-5">
            ${viewToggle('today')}
            ${resumeBanner()}${calBar()}${focusCard()}
            ${header(runSlotName(runSlot), `Today · ${WD[tw]} · ${dateStr}`)}
            ${picker()}
            <div class="grit-card p-5 mt-4">
              <div class="grit-eyebrow">Today's run</div>
              <div class="text-[15px] text-ink mt-1 leading-relaxed">${dirs || 'Easy run, go by feel.'}</div>
              ${doneToday ? `<div class="grit-chip-success mt-3 inline-flex items-center gap-1">${icon.check('w-3.5 h-3.5')} Logged</div>` : ''}
            </div>
            ${isViewingSelf() ? '<button id="logrun" class="grit-btn mt-3">Log this run</button>' : ''}
            <button id="openrun" class="w-full text-grit font-bold py-3 mt-1 text-[15px] inline-flex items-center justify-center gap-1">Open Running ${icon.right('w-3.5 h-3.5')}</button>
            <div class="h-24"></div>
          </div>${renderNav('home')}`;
        bindCommon();
        const lr = document.getElementById('logrun');
        if (lr) lr.onclick = () => openRunLog({ defaultType: runSlotType(runSlot), planContext: planContextFor(runSlot, plan), onSaved: async (r) => { await afterRunSaved(r); renderDay(el); } });
        document.getElementById('openrun').onclick = () => go('running');
        return;
      }
      const nextUp = allDays.find((d) => d.weekday > tw) || allDays[0];
      el.innerHTML = `${renderHeader()}
        <div class="grit-page px-5 pt-5">
          ${viewToggle('today')}
          ${resumeBanner()}${calBar()}${focusCard()}
          ${header('Rest day', `${WD[tw]} · ${dateStr}`)}
          ${picker()}
          <div class="grit-card p-5 mt-4">
            <div class="font-bold text-ink">Nothing scheduled today. Recover up.</div>
            <div class="text-[15px] text-inksoft mt-1">Next up: <span class="font-semibold text-ink">${nextUp ? WD[nextUp.weekday] + ' · ' + nextUp.name : 'none'}</span>. Want to train anyway? Pick a day above.</div>
          </div>
          <div class="h-24"></div>
        </div>${renderNav('home')}`;
      bindCommon(); return;
    }

    const day = selected;
    const isToday = day.weekday === tw;
    const eyebrow = `${isToday ? 'Today' : 'Make-up'} · ${WD[day.weekday]}${isToday ? ' · ' + dateStr : ''}`;

    if (day.kind === 'lift') {
      const exs = await loadDayExercises(day.id);
      const lasts = await Promise.all(exs.map((e) => loadLastSets(e.exercise_id, undefined, day.id)));
      const proj = projectMinutes(exs);
      const noteCount = exs.reduce((n, e) => n + (notesByEx[e.exercise_id]?.length || 0), 0);
      const rows = exs.map((e, i) => {
        const work = (lasts[i] || []).filter((s) => !s.is_warmup);
        const tracking = e.tracking || 'weight';
        const unit = tracking === 'time' ? 'sec' : 'reps';
        let lastVal, lastLbl;
        if (tracking === 'weight') {
          // The weight this lift starts at today (the progression engine keeps it current).
          const nextW = e.start_weight_lb ?? (work.length ? Math.max(...work.map((s) => s.weight_lb)) : null);
          lastVal = nextW != null ? fmtWeight(nextW) : '--'; lastLbl = work.length ? 'today' : 'start';
        } else if (tracking === 'time') {
          lastVal = work.length ? `${Math.max(...work.map((s) => Number(s.reps) || 0))}s` : '--';
          lastLbl = work.length ? 'last' : 'goal';
        } else {
          const lastReps = work.length ? Math.max(...work.map((s) => Number(s.reps) || 0)) : null;
          lastVal = lastReps != null ? `${lastReps} reps` : '--'; lastLbl = work.length ? 'last' : 'goal';
        }
        const self = isViewingSelf();
        return `<div ${self ? `data-editex="${e.id}"` : ''} class="flex items-center gap-3 grit-card p-3 mb-2 ${self ? 'active:scale-[0.99] transition-transform' : ''}">
          <div class="w-8 h-8 rounded-full bg-grit-soft text-grit-press text-[15px] font-semibold flex items-center justify-center flex-none">${i + 1}</div>
          <div class="flex-1 min-w-0"><div class="font-bold text-[15px] text-ink truncate">${displayNameHtml(e.exercise)}</div>
          <div class="text-[12px] text-inksoft">${e.target_sets} sets · ${repRange(e.rep_low, e.rep_high)} ${unit} · ${e.exercise.muscle_group || ''}</div>
          ${notesByEx[e.exercise_id] ? `<div class="text-[13px] text-warn font-medium truncate mt-0.5">Note: ${notesByEx[e.exercise_id][0].body.replace(/</g, '&lt;')}</div>` : ''}</div>
          <div class="text-right"><div class="font-bold text-[15px] text-ink">${lastVal}</div><div class="text-[12px] text-inksoft">${lastLbl}</div></div>
          ${self ? `<span class="text-inksoft flex-none">${icon.right('w-4 h-4')}</span>` : ''}
        </div>`;
      }).join('');
      const runSlot = runMap[day.weekday];
      const runDoneToday = runsToday.some((r) => r.type !== 'hiit');
      const runCard = (runSlot && isToday) ? `<div class="grit-card p-4 mt-3">
          <div class="grit-eyebrow">Also today · run</div>
          <div class="text-[15px] text-ink mt-1 leading-relaxed">${runSlotDirections(runSlot) || 'Easy run, go by feel.'}</div>
          ${runDoneToday ? `<div class="grit-chip-success mt-2 inline-flex items-center gap-1">${icon.check('w-3.5 h-3.5')} Logged</div>` : (isViewingSelf() ? `<button id="logrun2" class="text-grit font-bold py-2 mt-1 text-[15px] inline-flex items-center gap-1">Log this run ${icon.right('w-3.5 h-3.5')}</button>` : '')}
        </div>` : '';
      el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5">${viewToggle('today')}${resumeBanner()}${calBar()}${focusCard()}${header(day.name, eyebrow)}${picker()}
        ${bodyCard(exs.map((e) => e.exercise), settings?.body_sex)}
        <div class="flex items-center justify-between gap-2 mt-4 mb-2"><div class="text-[15px] text-inksoft">${exs.length} lifts · about ${proj} min</div>
          <button id="day-notes" class="grit-pill">Notes${noteCount ? ` (${noteCount})` : ''}</button></div>
        ${rows}<button id="start" class="grit-btn mt-3">Start workout</button>${runCard}<div class="h-24"></div></div>${renderNav('home')}`;
      bindCommon();
      document.getElementById('day-notes').onclick = () => openNotes({ title: day.name, lifts: exs.map((e) => ({ exercise_id: e.exercise_id, exercise: e.exercise })), dayId: day.id, date: todayISO(), readOnly: !isViewingSelf(), onChange: () => renderDay(el) });
      el.querySelectorAll('[data-editex]').forEach((b) => { b.onclick = () => { const pe = exs.find((x) => x.id === b.dataset.editex); if (pe) openExerciseEdit(pe, () => renderDay(el)); }; });
      document.getElementById('start').onclick = () => { sessionStorage.setItem('grit-day', day.id); go('logger'); };
      const lr2 = document.getElementById('logrun2');
      if (lr2) lr2.onclick = () => openRunLog({ defaultType: runSlotType(runSlot), planContext: planContextFor(runSlot, plan), onSaved: async (r) => { await afterRunSaved(r); renderDay(el); } });
      return;
    }

    if (day.kind === 'run') {
      const dirs = runDirections(day);
      const doneToday = runsToday.some((r) => r.type !== 'hiit');
      el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5">${viewToggle('today')}${resumeBanner()}${calBar()}${focusCard()}${header(day.name, eyebrow)}${picker()}
        <div class="grit-card p-5 mt-4">
          <div class="grit-eyebrow">Today's run</div>
          <div class="text-[15px] text-ink mt-1 leading-relaxed">${dirs || 'Easy run, go by feel.'}</div>
          ${doneToday ? `<div class="grit-chip-success mt-3 inline-flex items-center gap-1">${icon.check('w-3.5 h-3.5')} Logged</div>` : ''}
        </div>
        ${isViewingSelf() ? '<button id="logrun" class="grit-btn mt-3">Log this run</button>' : ''}
        <button id="openrun" class="w-full text-grit font-bold py-3 mt-1 text-[15px] inline-flex items-center justify-center gap-1">Open Running ${icon.right('w-3.5 h-3.5')}</button>
        <div class="h-24"></div></div>${renderNav('home')}`;
      bindCommon();
      const lr = document.getElementById('logrun');
      if (lr) lr.onclick = () => { const rs = runOnWeekday(day.weekday); openRunLog({ defaultType: rs ? runSlotType(rs.slot) : 'easy', planContext: rs ? planContextFor(rs.slot, plan) : null, onSaved: async (r) => { await afterRunSaved(r); renderDay(el); } }); };
      document.getElementById('openrun').onclick = () => go('running');
      return;
    }

    // hiit
    const doneToday = runsToday.some((r) => r.type === 'hiit');
    el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5">${viewToggle('today')}${resumeBanner()}${calBar()}${focusCard()}${header(day.name, eyebrow)}${picker()}
      <div class="grit-card p-5 mt-4">
        <div class="grit-eyebrow">HIIT day</div>
        <div class="text-[15px] text-ink mt-1">Do your HIIT video, then check it off.</div>
        ${doneToday ? `<div class="grit-chip-success mt-3 inline-flex items-center gap-1">${icon.check('w-3.5 h-3.5')} Done</div>` : ''}
      </div>
      ${isViewingSelf() && !doneToday ? '<button id="hiit-done" class="grit-btn mt-3">Mark HIIT done</button>' : ''}
      <div class="h-24"></div></div>${renderNav('home')}`;
    bindCommon();
    const hd = document.getElementById('hiit-done');
    if (hd) hd.onclick = async () => { await saveRun({ type: 'hiit', actual_minutes: null }); renderDay(el); };
  }
}

// ── Calendar ────────────────────────────────────────────────────────────────
function renderCalendar(el) {
  let monthOffset = 0;
  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5"><p class="text-inksoft">Loading…</p></div>${renderNav('home')}`;

  (async () => {
    const [allDays, sessions, runs, plan, foodByDate, settings, goals] = await Promise.all([loadProgramDays(), loadSessions(), loadRuns(), loadRunPlan(), loadFoodTotalsByDate(), loadSettings(), loadGoals()]);
    curRunWeek = weekForPlan(plan);
    const sessByDate = new Set(sessions.map((s) => s.date));
    const runByDate = new Set(runs.map((r) => r.date));
    let pickedDate = todayISO();
    let chosenId = null; // the workout picked for pickedDate (null = suggested)
    let movingId = null; // the logged workout whose date is being changed
    const dayFor = (wd) => allDays.find((d) => d.weekday === wd);
    const liftDays = allDays.filter((d) => d.kind === 'lift');
    const runMap = planRunMap(plan, allDays);
    paint();

    function paint() {
      const base = new Date(); base.setDate(1); base.setMonth(base.getMonth() + monthOffset);
      const year = base.getFullYear(); const month = base.getMonth();
      const monthName = base.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const firstDow = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const tIso = todayISO();

      const cells = [];
      for (let i = 0; i < firstDow; i++) cells.push('<div></div>');
      for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const wd = (() => { const g = new Date(year, month, d).getDay(); return g === 0 ? 7 : g; })();
        const sd0 = dayFor(wd);
        const sched = (sd0 && !(sd0.kind === 'lift' && doneThisWeek(sessions, sd0.id, iso))) ? sd0 : runMap[wd];
        const done = sessByDate.has(iso) || runByDate.has(iso);
        const isToday = iso === tIso;
        const isPast = iso < tIso;
        let dot = '';
        if (done) dot = '<span class="w-2.5 h-2.5 rounded-full bg-success"></span>';
        else if (sched && !isPast) dot = '<span class="w-2.5 h-2.5 rounded-full border-2 border-grit"></span>';
        else if (sched && isPast) dot = '<span class="w-2.5 h-2.5 rounded-full border border-hairline"></span>';
        const sel = iso === pickedDate;
        const ring = isToday ? 'ring-2 ring-grit' : sel ? 'ring-1 ring-hairline bg-pagebg' : '';
        cells.push(`<button data-date="${iso}" class="aspect-square rounded-xl flex flex-col items-center justify-center ${ring}">
          <span class="text-[15px] font-bold ${isToday ? 'text-grit' : 'text-ink'}">${d}</span>
          <span class="h-2.5 flex items-center mt-0.5">${dot}</span></button>`);
      }

      const wdSel = (() => { const [y, m, dd] = pickedDate.split('-').map(Number); const g = new Date(y, m - 1, dd).getDay(); return g === 0 ? 7 : g; })();
      const sd = dayFor(wdSel);
      const doneSel = sessByDate.has(pickedDate) || runByDate.has(pickedDate);
      const selLabel = new Date(pickedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
      const runSlotSel = runMap[wdSel];
      let action = '';
      const tIsoSel = todayISO();
      const logged = sessions.filter((s) => s.date === pickedDate);
      const loggedList = logged.map((s) => {
        const name = s.day?.name || 'Workout';
        if (movingId === s.id) return `<div class="mt-3 border-t border-hairline pt-3">
            <div class="text-[15px] font-semibold text-ink">Move ${name} to</div>
            <div class="flex gap-2 mt-2"><input id="move-date" type="date" max="${tIsoSel}" value="${pickedDate}" class="grit-input flex-1" aria-label="New date for ${name}" />
            <button id="move-save" class="grit-btn !w-auto px-5">Save</button></div>
            <button id="move-cancel" class="w-full text-inksoft text-[15px] font-semibold py-2 mt-1">Cancel</button></div>`;
        return `<div class="flex items-center justify-between gap-2 mt-2 min-h-[44px]">
            <div class="text-[15px] text-ink"><span class="text-success font-bold">Done:</span> ${name}${s.status === 'partial' ? ' (not finished)' : ''}</div>
            <button data-move="${s.id}" class="grit-pill flex-none">Change day</button></div>`;
      }).join('');
      // Any lift day can be done on a lift day or a free day (switch days, book order still advised p.320).
      if (liftDays.length && (sd?.kind === 'lift' || (!sd && !runSlotSel))) {
        const pickId = chosenId || suggestDay(liftDays, sessions, pickedDate, sd?.kind === 'lift' ? sd.id : null);
        const pick = liftDays.find((x) => x.id === pickId) || liftDays[0];
        const startISO = startDateFor(pickedDate, tIsoSel);
        const when = pickedDate > tIsoSel ? ' today' : pickedDate < tIsoSel ? ` for ${new Date(pickedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}` : '';
        const doneWk = doneThisWeek(sessions, pick.id, pickedDate);
        action = `<label class="block text-[13px] text-inksoft mt-3">Workout
            <select id="cal-pick" class="grit-input mt-1 font-semibold">${liftDays.map((x) => `<option value="${x.id}" ${x.id === pick.id ? 'selected' : ''}>${WD[x.weekday] || ''} · ${x.name}${doneThisWeek(sessions, x.id, pickedDate) ? ' (done this week)' : ''}</option>`).join('')}</select></label>
          ${doneWk && doneWk.date !== pickedDate ? `<div class="text-[13px] text-inksoft mt-1">Already done ${new Date(doneWk.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })} this week.</div>` : ''}
          <button id="cal-start" data-day="${pick.id}" data-date="${startISO}" class="grit-btn mt-3">${doneSel ? 'Log another: ' : 'Start '}${pick.name}${when}</button>`;
      }
      if (sd?.kind === 'lift') { /* handled above */ }
      else if (sd?.kind === 'run') action = `<button id="cal-run" class="grit-btn mt-3">Log this run</button><button id="cal-openrun" class="w-full text-grit font-bold py-2.5 mt-1 text-[15px] inline-flex items-center justify-center gap-1">Open Running ${icon.right('w-3.5 h-3.5')}</button>`;
      else if (sd?.kind === 'hiit') action = doneSel ? '' : '<button id="cal-hiit" class="grit-btn mt-3">Mark HIIT done</button>';
      else if (runSlotSel) action = `<button id="cal-run" class="grit-btn mt-3">Log this run</button><button id="cal-openrun" class="w-full text-grit font-bold py-2.5 mt-1 text-[15px] inline-flex items-center justify-center gap-1">Open Running ${icon.right('w-3.5 h-3.5')}</button>`;

      const loggedNames = logged.map((s) => s.day?.name).filter(Boolean);
      const detailName = loggedNames.length ? loggedNames.join(' + ') : sd ? sd.name : (runSlotSel ? runSlotName(runSlotSel) : 'Rest day');
      const movedTo = sd?.kind === 'lift' && !doneSel ? doneThisWeek(sessions, sd.id, pickedDate) : null;
      const detailKind = doneSel ? 'Logged' : movedTo ? `Done ${new Date(movedTo.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })} this week` : sd ? ({ lift: 'Lift day', run: 'Run day', hiit: 'HIIT day' }[sd.kind] || 'Workout') : (runSlotSel ? 'Run day' : 'No workout scheduled');
      const detail = `<div class="grit-card p-4 mt-4">
        <div class="grit-eyebrow">${selLabel}</div>
        <div class="font-bold text-[16px] text-ink mt-1">${detailName}</div>
        <div class="text-[13px] text-inksoft mt-0.5">${detailKind}</div>
        ${runSlotSel && !sd ? `<div class="text-[15px] text-inksoft mt-2 leading-relaxed">${runSlotDirections(runSlotSel)}</div>` : ''}
        ${foodByDate[pickedDate] ? `<div class="text-[13px] text-inksoft mt-1">Ate <b class="text-ink">${Math.round(foodByDate[pickedDate]).toLocaleString('en-US')}</b> cal</div>` : ''}
        ${isViewingSelf() ? loggedList + action : ''}</div>`;

      el.innerHTML = `${renderHeader()}
        <div class="grit-page px-5 pt-5">
          ${viewToggle('calendar')}
          <div class="flex items-center justify-between mb-3">
            <button id="prevm" aria-label="Previous month" class="text-grit w-11 h-11 -ml-2 flex items-center justify-center">${icon.left('w-5 h-5')}</button>
            <div class="font-bold text-ink">${monthName}</div>
            <button id="nextm" aria-label="Next month" class="text-grit w-11 h-11 -mr-2 flex items-center justify-center">${icon.right('w-5 h-5')}</button>
          </div>
          <div class="grid grid-cols-7 gap-1 text-center mb-1">${['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => `<div class="text-[12px] font-bold text-inksoft">${d}</div>`).join('')}</div>
          <div class="grid grid-cols-7 gap-1">${cells.join('')}</div>
          <div class="flex gap-3 mt-3 text-[12px] text-inksoft">
            <span class="flex items-center gap-1"><span class="w-2.5 h-2.5 rounded-full bg-success"></span>done</span>
            <span class="flex items-center gap-1"><span class="w-2.5 h-2.5 rounded-full border-2 border-grit"></span>scheduled</span>
          </div>
          ${detail}
          <div class="h-24"></div>
        </div>${renderNav('home')}`;

      bindToggle(el, renderDay);
      document.getElementById('prevm').onclick = () => { monthOffset--; paint(); };
      document.getElementById('nextm').onclick = () => { monthOffset++; paint(); };
      el.querySelectorAll('button[data-date]:not(#cal-start)').forEach((b) => b.onclick = () => { pickedDate = b.dataset.date; chosenId = null; movingId = null; paint(); });
      const cs = document.getElementById('cal-start');
      if (cs) cs.onclick = () => { sessionStorage.setItem('grit-day', cs.dataset.day); sessionStorage.setItem('grit-day-date', cs.dataset.date); go('logger'); };
      const cp = document.getElementById('cal-pick');
      if (cp) cp.onchange = () => { chosenId = cp.value; paint(); };
      el.querySelectorAll('[data-move]').forEach((b) => b.onclick = () => { movingId = b.dataset.move; paint(); });
      const mc = document.getElementById('move-cancel');
      if (mc) mc.onclick = () => { movingId = null; paint(); };
      const ms = document.getElementById('move-save');
      if (ms) ms.onclick = async () => {
        const to = document.getElementById('move-date').value;
        if (!to || to > todayISO()) { toast('Pick today or an earlier day', { tone: 'info' }); return; }
        const s = sessions.find((x) => x.id === movingId);
        if (!(await moveSession(movingId, to))) { toast("Couldn't move it, try again", { tone: 'bad' }); return; }
        s.date = to; movingId = null; sessByDate.clear(); sessions.forEach((x) => sessByDate.add(x.date));
        pickedDate = to; toast('Moved'); paint();
      };
      const cr = document.getElementById('cal-run');
      if (cr) cr.onclick = () => openRunLog({ defaultType: runSlotSel ? runSlotType(runSlotSel) : 'easy', defaultDate: pickedDate, planContext: runSlotSel ? planContextFor(runSlotSel, plan) : null, onSaved: async (r) => { await afterRunSaved(r); renderDay(el); } });
      const cor = document.getElementById('cal-openrun');
      if (cor) cor.onclick = () => go('running');
      const ch = document.getElementById('cal-hiit');
      if (ch) ch.onclick = async () => { await saveRun({ type: 'hiit', date: pickedDate, actual_minutes: null }); renderDay(el); };
    }
  })();
}

function bindToggle(el, rerender) {
  el.querySelectorAll('[data-view]').forEach((b) => b.onclick = () => { setHomeView(b.dataset.view); rerender(el); });
}
